import { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { useContracts } from './useContracts';

const eq = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

const EMPTY = {
  loaded: false,
  isOwner: false,
  isAdmin: false,
  canAccess: false,
  role: null,
  details: {
    plans:       { ownerMatch: false, adminMatch: false, owner: '', admin: '' },
    coreStorage: { ownerMatch: false, adminMatch: false, owner: '', admin: '' },
    booster:     { ownerMatch: false, adminMatch: false, owner: '', admin: '' }
  }
};

/**
 * Read owner & admin from Plans, CoreStorage, Booster.
 * The connected wallet has admin-panel access if it matches owner or admin
 * on ANY of the three contracts. Role is set to 'owner' if any owner match,
 * else 'admin' if any admin match.
 */
export const useAdminGuard = (refreshKey = 0) => {
  const { account } = useWallet();
  const { plans, coreStorage, booster } = useContracts();
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!account || !plans?.read || !coreStorage?.read || !booster?.read) {
        if (!cancelled) setState({ ...EMPTY, loaded: true });
        return;
      }

      try {
        const [
          pOwner, pAdmin,
          cOwner, cAdmin,
          bOwner, bAdmin
        ] = await Promise.all([
          plans.read.owner().catch(() => ''),
          plans.read.admin().catch(() => ''),
          coreStorage.read.owner().catch(() => ''),
          coreStorage.read.admin().catch(() => ''),
          booster.read.owner().catch(() => ''),
          booster.read.admin().catch(() => '')
        ]);

        const details = {
          plans:       { owner: pOwner, admin: pAdmin, ownerMatch: eq(account, pOwner), adminMatch: eq(account, pAdmin) },
          coreStorage: { owner: cOwner, admin: cAdmin, ownerMatch: eq(account, cOwner), adminMatch: eq(account, cAdmin) },
          booster:     { owner: bOwner, admin: bAdmin, ownerMatch: eq(account, bOwner), adminMatch: eq(account, bAdmin) }
        };

        const isOwner =
          details.plans.ownerMatch ||
          details.coreStorage.ownerMatch ||
          details.booster.ownerMatch;
        const isAdmin =
          details.plans.adminMatch ||
          details.coreStorage.adminMatch ||
          details.booster.adminMatch;

        if (!cancelled) {
          setState({
            loaded: true,
            isOwner,
            isAdmin,
            canAccess: isOwner || isAdmin,
            role: isOwner ? 'owner' : isAdmin ? 'admin' : null,
            details
          });
        }
      } catch {
        if (!cancelled) setState({ ...EMPTY, loaded: true });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [account, plans, coreStorage, booster, refreshKey]);

  return state;
};
